import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, X, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";

export default function CheckoutSuccess() {
    const [sp] = useSearchParams();
    const sessionId = sp.get("session_id");
    const orderId = sp.get("order_id");
    const { user } = useAuth();
    const [status, setStatus] = React.useState(orderId && !sessionId ? "cod" : "polling");
    const [paymentData, setPaymentData] = React.useState(null);
    const { clear } = useCart();
    const attempts = React.useRef(0);

    React.useEffect(() => {
        if (orderId && !sessionId) {
            clear();
            return;
        }
        if (!sessionId) { setStatus("error"); return; }
        let stopped = false;
        const poll = async () => {
            try {
                const res = await api.get(`/payments/status/${sessionId}`);
                if (stopped) return;
                setPaymentData(res.data);
                if (res.data.payment_status === "paid") {
                    setStatus("paid");
                    clear();
                    return;
                }
                if (res.data.status === "expired") { setStatus("expired"); return; }
                if (attempts.current >= 10) { setStatus("timeout"); return; }
                attempts.current += 1;
                setTimeout(poll, 2000);
            } catch {
                if (!stopped) setStatus("error");
            }
        };
        poll();
        return () => { stopped = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId, orderId]);

    return (
        <div className="container-x py-20 text-center max-w-xl mx-auto" data-testid="checkout-success">
            {status === "polling" && (
                <>
                    <Loader2 className="h-16 w-16 mx-auto animate-spin text-primary mb-6" />
                    <h1 className="font-heading text-3xl mb-3">Verificăm plata...</h1>
                    <p className="text-muted-foreground">Te rugăm să aștepți.</p>
                </>
            )}
            {(status === "paid" || status === "cod") && (
                <>
                    <div className="w-20 h-20 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-6"><Check className="h-10 w-10" /></div>
                    <h1 className="font-heading text-4xl mb-3" data-testid="checkout-success-title">{status === "cod" ? "Comandă plasată!" : "Plată reușită!"}</h1>
                    <p className="text-muted-foreground mb-6">
                        {status === "cod" ? "Comanda ta a fost înregistrată. Te vom contacta pentru confirmare și livrare. Plata se face la primire." : "Mulțumim pentru comandă. Vei primi un email cu detaliile."}
                    </p>
                    {orderId && <div className="text-sm mb-2 text-muted-foreground">Număr comandă: <span className="font-mono font-medium text-foreground">#{orderId.slice(-10)}</span></div>}
                    {paymentData && <div className="text-sm mb-6">Sumă: {((paymentData.amount_total || 0) / 100).toFixed(2)} {(paymentData.currency || "").toUpperCase()}</div>}
                    <div className="flex flex-wrap gap-3 justify-center">
                        {user ? (
                            <Link to="/cont?tab=orders" className="btn-primary" data-testid="go-to-orders-btn">Comenzile mele</Link>
                        ) : (
                            <Link to="/" className="btn-primary" data-testid="go-home-btn">Înapoi acasă</Link>
                        )}
                        <Link to="/magazin" className="btn-outline" data-testid="continue-shopping-btn">Continuă cumpărăturile</Link>
                    </div>
                </>
            )}
            {(status === "expired" || status === "timeout" || status === "error") && (
                <>
                    <div className="w-20 h-20 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center mx-auto mb-6"><X className="h-10 w-10" /></div>
                    <h1 className="font-heading text-3xl mb-3">Plată neconfirmată</h1>
                    <p className="text-muted-foreground mb-6">Dacă banii au fost debitați, contactează-ne pentru verificare.</p>
                    <Link to="/cos" className="btn-primary">Înapoi la coș</Link>
                </>
            )}
        </div>
    );
}
